"use client";

import * as React from "react";
import { Clock } from "lucide-react";
import { OTPInput, type SlotProps } from "input-otp";
import { Button } from "@gerama/ui";
import { cn, formatDuration } from "@gerama/utils";
import { env } from "@/env";
import { GoogleSignInButton } from "./google-sign-in-button";
import { AuthLogo, FormError, OTP_LENGTH, OrDivider, RESEND_SECONDS, maskMobile, toLatinDigits } from "./auth-bits";

export function AuthOtpView({
  mobile,
  resetSignal,
  onSubmit,
  onResend,
  onLoginWithPassword,
  onForgotPassword,
  onBack,
  isLoading,
  error,
}: {
  mobile: string;
  resetSignal: number;
  onSubmit: (code: string) => void;
  onResend: () => void;
  onLoginWithPassword: () => void;
  onForgotPassword: () => void;
  onBack: () => void;
  isLoading: boolean;
  error?: string;
}) {
  const [code, setCode] = React.useState("");
  const [seconds, setSeconds] = React.useState(RESEND_SECONDS);
  const sentRef = React.useRef<string | null>(null);

  React.useEffect(() => {
    if (seconds <= 0) return;
    const id = window.setInterval(() => setSeconds((value) => value - 1), 1000);
    return () => window.clearInterval(id);
  }, [seconds]);

  const isFirstReset = React.useRef(true);
  React.useEffect(() => {
    if (isFirstReset.current) {
      isFirstReset.current = false;
      return;
    }
    sentRef.current = null;
    setCode("");
  }, [resetSignal]);

  const submit = (value: string) => {
    const digits = toLatinDigits(value).replace(/\D/g, "").slice(0, OTP_LENGTH);
    if (digits.length !== OTP_LENGTH || isLoading || sentRef.current === digits) return;
    sentRef.current = digits;
    onSubmit(digits);
  };

  return (
    <div>
      <AuthLogo />
      <p className="text-muted-foreground mt-5 mb-4 text-sm">
        کد فرستاده شده برای {maskMobile(mobile)} را وارد کنید.
      </p>
      <form
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          submit(code);
        }}
      >
        <div className="mb-4 flex justify-center" dir="ltr">
          <OTPInput
            maxLength={OTP_LENGTH}
            value={code}
            autoFocus
            containerClassName="gap-3"
            onChange={(value) => {
              const digits = toLatinDigits(value).replace(/\D/g, "").slice(0, OTP_LENGTH);
              setCode(digits);
              if (digits.length < OTP_LENGTH) {
                sentRef.current = null;
                return;
              }
              submit(digits);
            }}
            render={({ slots }) => (
              <>
                {slots.map((slot, index) => (
                  <OtpSlot key={index} {...slot} />
                ))}
              </>
            )}
          />
        </div>
        <Button type="submit" className="mb-3 h-11 w-full rounded-lg" disabled={isLoading || code.length !== OTP_LENGTH}>
          {isLoading ? "..." : "ادامه"}
        </Button>
      </form>
      <div className="text-muted-foreground mb-4 flex items-center justify-between text-sm">
        <span className="flex items-center gap-1.5">
          <Clock className="size-4" />
          <span aria-live="polite">{formatDuration(Math.max(seconds, 0))}</span>
        </span>
        <button
          type="button"
          className="text-destructive hover:underline disabled:opacity-50 disabled:no-underline"
          disabled={seconds > 0 || isLoading}
          onClick={() => {
            setSeconds(RESEND_SECONDS);
            sentRef.current = null;
            onResend();
          }}
        >
          ارسال مجدد
        </button>
      </div>
      <FormError message={error} />
      <div className="flex flex-wrap items-center gap-4 text-sm">
        <Button type="button" variant="outline" className="h-11 flex-1 font-light" onClick={onForgotPassword}>
          رمز عبور را فراموش کردید؟
        </Button>
        <Button type="button" variant="outline" className="h-11 flex-1 font-light" onClick={onLoginWithPassword}>
          ورود با رمز عبور
        </Button>
      </div>
      {env.NEXT_PUBLIC_GOOGLE_CLIENT_ID ? (
        <>
          <OrDivider />
          <GoogleSignInButton disabled={isLoading} />
        </>
      ) : null}
      <Button type="button" variant="ghost" className="mt-4 h-11 w-full font-light" disabled={isLoading} onClick={onBack}>
        بازگشت
      </Button>
    </div>
  );
}

function OtpSlot({ char, isActive }: SlotProps) {
  return (
    <div
      className={cn(
        "border-input bg-muted/50 flex h-[60px] w-[50px] items-center justify-center rounded-md border text-xl font-black",
        isActive && "border-ring ring-ring/50 ring-2",
      )}
    >
      {char}
    </div>
  );
}
